import { ValidationRules } from './validation-configs';
import { Injectable } from '@angular/core';
import { FieldRule, LocationFeatureFlags, PageRuleSet } from './validation';
import { CommonPatterns } from './validation-patterns';
import { AbstractControl } from '@angular/forms';

@Injectable({ providedIn: 'root' })
export class ValidationRulesService {
  getRules(locationCode: string, pageName: string): PageRuleSet {
    const loc = ValidationRules[locationCode] ?? ValidationRules.Default;
    return loc.pages?.[pageName] ?? {};
  }

  getRulesByHub(
    countryCode: string | null | undefined,
    hubId: number | null | undefined,
    pageName: string,
  ): PageRuleSet {
    const locationCode = this.resolveLocationCode(countryCode, hubId);
    return this.getRules(locationCode, pageName);
  }

  getFieldRules(locationCode: string, pageName: string, section: string, field: string): FieldRule | null {
    const pageRules = this.getRules(locationCode, pageName);
    return pageRules?.[section]?.[field] ?? null;
  }

  validatePage(
    formValue: Record<string, Record<string, unknown>>,
    locationCode: string,
    pageName: string,
    pageRulesOverride?: PageRuleSet,
  ): { isValid: boolean; errors: Record<string, string[]> } {
    const pageRules = pageRulesOverride ?? this.getRules(locationCode, pageName);
    return this.runValidation(formValue, pageRules);
  }

  validateStep(
    formValue: Record<string, Record<string, unknown>>,
    pageRules: PageRuleSet,
  ): { isValid: boolean; errors: Record<string, string[]> } {
    return this.runValidation(formValue, pageRules);
  }

  getFeatureFlag<K extends keyof LocationFeatureFlags>(
    countryCode: string | null | undefined,
    hubId: number | null | undefined,
    flag: K,
  ): boolean {
    const locationCode = this.resolveLocationCode(countryCode, hubId);
    const loc = ValidationRules[locationCode] ?? ValidationRules.Default;
    return loc.featureFlags?.[flag] ?? ValidationRules.Default.featureFlags?.[flag] ?? false;
  }

  public resolveLocationCode(countryCode: string | null | undefined, hubId: number | null | undefined): string {
    const normalizedCountryCode = countryCode?.trim()?.toUpperCase();

    if (normalizedCountryCode && hubId !== null && hubId !== undefined) {
      const exactLocationCode = `${normalizedCountryCode}-${hubId}`;

      if (ValidationRules[exactLocationCode]) {
        return exactLocationCode;
      }
    }

    if (normalizedCountryCode && ValidationRules[normalizedCountryCode]) {
      return normalizedCountryCode;
    }

    return 'Default';
  }

  private runValidation(
    formValue: Record<string, Record<string, unknown>>,
    pageRules: PageRuleSet,
  ): { isValid: boolean; errors: Record<string, string[]> } {
    const result = {
      isValid: true,
      errors: {} as Record<string, string[]>,
    };

    for (const sectionName of Object.keys(pageRules)) {
      const sectionRules = pageRules[sectionName];
      const sectionValue = formValue?.[sectionName] ?? {};

      for (const fieldName of Object.keys(sectionRules)) {
        const rule = sectionRules[fieldName];
        const value = sectionValue[fieldName];
        const errors = this.validateField(rule, value, formValue, sectionValue);

        if (errors.length > 0) {
          result.isValid = false;
          result.errors[`${sectionName}.${fieldName}`] = errors;
        }
      }
    }

    return result;
  }

  private validateField(
    rule: FieldRule,
    value: unknown,
    formValue: Record<string, Record<string, unknown>>,
    sectionValue: Record<string, unknown>,
  ): string[] {
    const fieldErrors: string[] = [];

    // required
    if (rule.required) {
      const isEmptyArray = Array.isArray(value) && value.length === 0;
      const isEmpty = value === null || value === undefined || value === '' || isEmptyArray;

      if (isEmpty) {
        fieldErrors.push('required');
      }
    }

    // pattern
    if (rule.patternKey && typeof value === 'string' && value !== '') {
      const patternKey = String(rule.patternKey);
      const pattern = CommonPatterns[rule.patternKey];

      if (!pattern) {
        fieldErrors.push('pattern.default');
      } else if (!pattern.test(value)) {
        fieldErrors.push(`pattern.${patternKey}`);
      }
    }

    // custom validator
    if (rule.customValidator && value !== null && value !== '') {
      const customResult = rule.customValidator(value, { formValue, sectionValue });
      if (customResult !== true) {
        fieldErrors.push(typeof customResult === 'string' ? customResult : 'invalid');
      }
    }

    return fieldErrors;
  }
}

export function getRequiredAsterisk(control: AbstractControl | null, fieldRule?: FieldRule | null): string {
  if (fieldRule?.required) {
    return ' *';
  }

  if (control?.validator) {
    const validator = control.validator({} as AbstractControl);
    if (validator?.required) {
      return ' *';
    }
  }

  return '';
}
